/**
 * Runtime config (files + env vars) hooks for a feature, using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import type { RuntimeConfigCtx } from '../api/adminApi';
import type { RuntimeConfigList } from '../schemas/admin.schemas';
import { invalidateMissingRuntimeConfig } from './useMissingRuntimeConfig';

const runtimeConfigKey = (
  featureId: string,
  ctx?: RuntimeConfigCtx,
): (string | undefined)[] => ['runtime-config', featureId, ctx?.projectId, ctx?.packId];

export function useRuntimeConfig(featureId: string, ctx?: RuntimeConfigCtx) {
  return useQuery<RuntimeConfigList>({
    queryKey: runtimeConfigKey(featureId, ctx),
    queryFn: () => adminApi.getRuntimeConfig(featureId, ctx),
    enabled: featureId.length > 0,
  });
}

interface UpsertArgs {
  featureId: string;
  kind: 'file' | 'env';
  name: string;
  payload: {
    value: string;
    target_path?: string;
    file_mode?: string;
    secret?: boolean;
  };
  ctx?: RuntimeConfigCtx;
}

export function useUpsertRuntimeConfigValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, kind, name, payload, ctx }: UpsertArgs) =>
      adminApi.putRuntimeConfigValue(featureId, kind, name, payload, ctx),
    onSuccess: (_data, { featureId }) => {
      // Invalidate the feature-level prefix to clear all scopes (global + pack + project views).
      // Global/pack values layer into narrower merged views, so a write changes all cached scopes.
      void queryClient.invalidateQueries({
        queryKey: ['runtime-config', featureId],
      });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

interface DeleteArgs {
  featureId: string;
  kind: 'file' | 'env';
  name: string;
  ctx?: RuntimeConfigCtx;
}

export function useDeleteRuntimeConfigValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, kind, name, ctx }: DeleteArgs) =>
      adminApi.deleteRuntimeConfigValue(featureId, kind, name, ctx),
    onSuccess: (_data, { featureId }) => {
      // Invalidate the feature-level prefix to clear all scopes (global + pack + project views).
      // Global/pack values layer into narrower merged views, so a write changes all cached scopes.
      void queryClient.invalidateQueries({
        queryKey: ['runtime-config', featureId],
      });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

interface RevertArgs {
  featureId: string;
  ctx: RuntimeConfigCtx;
}

export function useRevertRuntimeConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, ctx }: RevertArgs) =>
      adminApi.revertRuntimeConfig(featureId, ctx),
    onSuccess: (_data, { featureId }) => {
      void queryClient.invalidateQueries({ queryKey: ['runtime-config', featureId] });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}
